import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@sea-snag/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { useOrganization } from '../contexts/organization-context';
import { compSetsApi, marketDataApi } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

interface AddToCompSetProps {
  /** Market boat pids (from Tracked tab) — used directly */
  marketBoatPids?: string[];
  /** MMK external IDs (from Browse tab) — bulk-tracked first */
  externalIds?: string[];
  /** Called after successful add (e.g. to clear selection) */
  onSuccess?: () => void;
}

function useAddToCompSet({ marketBoatPids, externalIds, onSuccess }: AddToCompSetProps) {
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const orgPid = organization?.pid ?? '';

  const { data: compSetsData, isLoading } = useQuery({
    queryKey: queryKeys.compSets.list(orgPid),
    queryFn: () => compSetsApi.list({ limit: 50 }),
    enabled: !!orgPid,
    staleTime: 30_000,
  });

  const compSets = compSetsData?.data ?? [];

  const addMutation = useMutation({
    mutationFn: async (compSetPid: string) => {
      let pids = marketBoatPids ?? [];

      // Browse tab: bulk track first to get pids
      if (externalIds?.length) {
        const tracked = await marketDataApi.track(externalIds);
        pids = tracked.map((t) => t.id);
        void queryClient.invalidateQueries({ queryKey: queryKeys.market.browseAll });
        void queryClient.invalidateQueries({ queryKey: queryKeys.market.trackedAll(orgPid) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.market.stats(orgPid) });
      }

      const result = await compSetsApi.addBoatsBulk(compSetPid, { marketBoatPids: pids });
      return result;
    },
    onSuccess: (result, compSetPid) => {
      const setName = compSets.find((s) => s.id === compSetPid)?.name ?? 'competitor set';
      const count = result.added;
      toast.success(count > 1 ? `${count} boats added to ${setName}` : `Boat added to ${setName}`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.compSets.all });
      onSuccess?.();
    },
    onError: () => toast.error('Failed to add to competitor set'),
  });

  return { orgPid, compSets, isLoading, addMutation };
}

function CompSetItems({
  compSets,
  isLoading,
  isPending,
  onSelect,
}: {
  compSets: { id: string; name: string; referenceBoats: Array<{ id: string; name: string }> }[];
  isLoading: boolean;
  isPending: boolean;
  onSelect: (id: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-3">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  if (compSets.length === 0) {
    return (
      <div className="px-2 py-3 text-center text-xs text-muted-foreground">
        No competitor sets yet.
        <br />
        Create one from the Competitor Sets page.
      </div>
    );
  }

  return (
    <>
      {compSets.map((set) => (
        <DropdownMenuItem
          key={set.id}
          disabled={isPending}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(set.id);
          }}
        >
          <div className="flex-1">
            <p>{set.name}</p>
            {set.referenceBoats.length > 0 && (
              <p className="text-xs text-muted-foreground">
                For: {set.referenceBoats.map((b) => b.name).join(', ')}
              </p>
            )}
          </div>
          {isPending && <Loader2 className="size-3 animate-spin" />}
        </DropdownMenuItem>
      ))}
    </>
  );
}

/** Use inside a DropdownMenuContent — renders comp sets as flat items with a label header */
export function AddToCompSetItems(props: AddToCompSetProps) {
  const { orgPid, compSets, isLoading, addMutation } = useAddToCompSet(props);

  if (!orgPid) return null;

  return (
    <>
      <DropdownMenuLabel>Add to Competitor Set</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <CompSetItems
        compSets={compSets}
        isLoading={isLoading}
        isPending={addMutation.isPending}
        onSelect={(id) => addMutation.mutate(id)}
      />
    </>
  );
}
